import { usePageTitle } from '@/hooks/usePageTitle';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Plus,
  SlidersHorizontal,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import productApi from '@/lib/productApi';
import { getHubSpotContext } from '@/lib/hubspotContext';
import { toast } from '@/lib/toast';
import RatesDialog from '@/components/products/RatesDialog';
import ReviewerGroupsDialog from '@/components/products/ReviewerGroupsDialog';
import ProductToolbar from '@/components/products/ProductToolbar';
import ProductTable from '@/components/products/ProductTable';
import BulkActionBar from '@/components/products/BulkActionBar';
import BulkPublishDialog from '@/components/products/BulkPublishDialog';
import ProductReviewersDialog from '@/components/products/ProductReviewersDialog';
import {
  groupProducts,
  rangeBetween,
  visibleOrder,
  type GroupBy,
  type ProductGroup,
} from '@/components/products/productListView';
import type {
  BulkOutcome,
  ProductListItem,
  ProductStatusCounts,
  ProductStatusFilter,
} from '@/types/product';

const COLLAPSED_KEY = 'products.collapsedGroups.v1';
const GROUP_BY_KEY = 'products.groupBy.v1';

/** Ingeklapte groepen zijn een werkvoorkeur, geen inhoud: die hoort per browser. */
const readCollapsed = (): Record<string, string[]> => {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string[]>) : {};
  } catch {
    return {};
  }
};

const readGroupBy = (): GroupBy => {
  const stored = localStorage.getItem(GROUP_BY_KEY);
  return stored === 'status' || stored === 'none' ? stored : 'category';
};

function ProductManagementPage() {
  usePageTitle('Producten');
  const navigate = useNavigate();

  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [taxonomy, setTaxonomy] = useState<{
    categories: string[];
    labels: string[];
    counts: ProductStatusCounts | null;
  }>({ categories: [], labels: [], counts: null });

  const [status, setStatus] = useState<ProductStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [labels, setLabels] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<GroupBy>(readGroupBy);
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(readCollapsed()[readGroupBy()] ?? []),
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const anchorRef = useRef<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const [ratesOpen, setRatesOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [reviewersOpen, setReviewersOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');

  /**
   * Terug naar de offertes met portalId en dealId erbij. Die staan ook in de
   * localStorage, maar binnen de HubSpot-card is de URL de betrouwbaarste bron;
   * createNew nemen we bewust niet mee, anders begint HomePage een nieuwe offerte.
   */
  const quotesHref = useMemo(() => {
    const context = getHubSpotContext();
    const params = new URLSearchParams();
    if (context.portalId) params.set('portalId', context.portalId);
    if (context.dealId) params.set('dealId', context.dealId);
    const query = params.toString();
    return query ? `/quotes?${query}` : '/quotes';
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, tax] = await Promise.all([
        productApi.list({
          search: search || undefined,
          category: category || undefined,
          labels,
          status,
        }),
        productApi.taxonomy(),
      ]);
      setProducts(list);
      setTaxonomy(tax);
      // Wat je niet meer ziet, kun je ook niet meer bulk-bewerken: anders zou
      // "publiceren" iets raken dat buiten het filter valt.
      const visible = new Set(list.map((product) => product.id));
      setSelected((current) => new Set([...current].filter((id) => visible.has(id))));
    } catch (error) {
      toast.error('Producten konden niet geladen worden', { error });
    } finally {
      setLoading(false);
    }
  }, [search, category, labels, status]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  /**
   * Echt leeg, niet "leeg door een filter". De teller uit de taxonomie kijkt
   * langs de filters heen; zonder dat onderscheid zou "nog geen producten" ook
   * verschijnen bij een zoekopdracht zonder treffers.
   */
  const catalogEmpty = taxonomy.counts !== null && taxonomy.counts.all === 0;

  const groups = useMemo(() => groupProducts(products, groupBy), [products, groupBy]);
  const grouped = groupBy !== 'none';
  const order = useMemo(() => visibleOrder(groups, collapsed), [groups, collapsed]);
  const allCollapsed = grouped && groups.length > 0 && groups.every((g) => collapsed.has(g.key));

  const persistCollapsed = (next: Set<string>, mode: GroupBy) => {
    const stored = readCollapsed();
    stored[mode] = [...next];
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify(stored));
  };

  const handleGroupByChange = (next: GroupBy) => {
    setGroupBy(next);
    localStorage.setItem(GROUP_BY_KEY, next);
    setCollapsed(new Set(readCollapsed()[next] ?? []));
  };

  const toggleCollapse = (key: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      persistCollapsed(next, groupBy);
      return next;
    });
  };

  const toggleCollapseAll = () => {
    const next = allCollapsed ? new Set<string>() : new Set(groups.map((group) => group.key));
    setCollapsed(next);
    persistCollapsed(next, groupBy);
  };

  /**
   * Shift breidt uit vanaf de vorige klik, over de rijen die je op dat moment
   * ziet. Zonder shift verspringt het anker, zodat een volgende reeks vanaf hier
   * loopt en niet vanaf iets van drie handelingen geleden.
   */
  const toggleProduct = (id: string, shiftKey: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (shiftKey && anchorRef.current && anchorRef.current !== id) {
        const range = rangeBetween(order, anchorRef.current, id);
        const select = !next.has(id);
        for (const entry of range) {
          if (select) next.add(entry);
          else next.delete(entry);
        }
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      anchorRef.current = id;
      return next;
    });
  };

  const toggleGroupSelection = (group: ProductGroup, select: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      for (const product of group.products) {
        if (select) next.add(product.id);
        else next.delete(product.id);
      }
      return next;
    });
  };

  const toggleLabel = (label: string) =>
    setLabels((current) =>
      current.includes(label) ? current.filter((entry) => entry !== label) : [...current, label],
    );

  const setRowEnabled = async (product: ProductListItem, enabled: boolean) => {
    setBusyIds((current) => new Set(current).add(product.id));
    try {
      await productApi.setEnabled(product.id, enabled);
      await load();
    } catch (error) {
      toast.error(`${product.name} kon niet bijgewerkt worden`, { error });
    } finally {
      setBusyIds((current) => {
        const next = new Set(current);
        next.delete(product.id);
        return next;
      });
    }
  };

  /** Eén plek waar een bulkantwoord een melding wordt, zodat "deels gelukt" niet stil blijft. */
  const reportOutcome = (outcome: BulkOutcome, verb: string) => {
    if (outcome.failed.length === 0) {
      toast.success(`${outcome.ok.length} ${verb}`);
      return;
    }
    const first = outcome.failed[0];
    toast[outcome.ok.length ? 'warning' : 'error'](
      `${outcome.ok.length} ${verb}, ${outcome.failed.length} overgeslagen — ${first.name}: ${first.error}`,
    );
  };

  const runBulk = async (
    input: Parameters<typeof productApi.bulk>[0],
    verb: string,
  ) => {
    setBulkBusy(true);
    try {
      reportOutcome(await productApi.bulk(input), verb);
      await load();
    } catch (error) {
      toast.error('De actie is niet gelukt', { error });
    } finally {
      setBulkBusy(false);
    }
  };

  const selectedIds = useMemo(() => [...selected], [selected]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const created = await productApi.create({ name: newName.trim() });
      setNewOpen(false);
      setNewName('');
      navigate(`/products/${created.id}`);
    } catch (error) {
      toast.error('Product aanmaken is niet gelukt', { error });
    }
  };

  const reviewCount = taxonomy.counts?.review ?? 0;

  return (
    <div className="mx-auto max-w-[1400px] space-y-5 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate(quotesHref)}
            className="-ml-1 mb-1 inline-flex items-center gap-1.5 rounded px-1 py-0.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none"
          >
            <ArrowLeft className="h-4 w-4" />
            Terug naar offertes
          </button>
          <h1 className="text-2xl font-semibold">Producten</h1>
          <p className="text-sm text-muted-foreground">
            {taxonomy.counts ? `${taxonomy.counts.all} producten · ` : ''}
            wijzigingen zijn pas zichtbaar in offertes zodra je ze publiceert.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setRatesOpen(true)}>
            <SlidersHorizontal className="mr-2 h-4 w-4" />
            Tarieven
          </Button>
          <Button variant="outline" onClick={() => setGroupsOpen(true)}>
            <Users className="mr-2 h-4 w-4" />
            Keuzegroepen
          </Button>
          <Button onClick={() => setNewOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nieuw product
          </Button>
        </div>
      </div>

      {reviewCount > 0 && status !== 'review' && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>
              {reviewCount} producten staan als concept met de markering &quot;nakijken&quot;,
              meestal uit de import. Ze zijn nog niet te kiezen in offertes.
            </span>
            <Button variant="outline" size="sm" onClick={() => setStatus('review')}>
              Toon alleen die
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <ProductToolbar
        status={status}
        counts={taxonomy.counts}
        onStatusChange={setStatus}
        search={search}
        onSearchChange={setSearch}
        categories={taxonomy.categories}
        category={category}
        onCategoryChange={setCategory}
        labels={taxonomy.labels}
        selectedLabels={labels}
        onToggleLabel={toggleLabel}
        groupBy={groupBy}
        onGroupByChange={handleGroupByChange}
        allCollapsed={allCollapsed}
        onToggleCollapseAll={toggleCollapseAll}
      />

      {loading ? (
        <p className="text-muted-foreground">Laden…</p>
      ) : products.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center">
            {catalogEmpty ? (
              // Een lege catalogus is iets anders dan een filter zonder treffers:
              // hier is niet "niets gevonden" het probleem maar "er staat nog
              // niets", en dan hoort de volgende stap hier te staan en niet in
              // een menu ergens bovenaan.
              <div className="mx-auto max-w-md space-y-4">
                <h2 className="text-lg font-semibold">Nog geen producten</h2>
                <p className="text-sm text-muted-foreground">
                  Voeg een product toe om de catalogus te vullen. Het is pas in offertes te kiezen
                  nadat het is gepubliceerd.
                </p>
                <Button onClick={() => setNewOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Nieuw product
                </Button>
              </div>
            ) : (
              <p className="text-muted-foreground">Geen producten gevonden met deze filters.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <ProductTable
          groups={groups}
          grouped={grouped}
          collapsed={collapsed}
          selected={selected}
          busyIds={busyIds}
          onToggleCollapse={toggleCollapse}
          onToggleGroup={toggleGroupSelection}
          onToggleProduct={toggleProduct}
          onOpen={(id) => navigate(`/products/${id}`)}
          onSetEnabled={setRowEnabled}
        />
      )}

      <BulkActionBar
        products={products}
        selected={selected}
        labels={taxonomy.labels}
        busy={bulkBusy}
        onPublish={() => setPublishOpen(true)}
        onSetEnabled={(enabled) =>
          void runBulk(
            { ids: selectedIds, action: enabled ? 'enable' : 'disable' },
            enabled ? 'ingeschakeld' : 'uitgeschakeld',
          )
        }
        onSetArchived={(archived) =>
          void runBulk(
            { ids: selectedIds, action: archived ? 'archive' : 'unarchive' },
            archived ? 'gearchiveerd' : 'uit het archief gehaald',
          )
        }
        onAddLabel={(label) =>
          void runBulk({ ids: selectedIds, action: 'add-label', label }, 'van een label voorzien')
        }
        onReviewers={() => setReviewersOpen(true)}
        onClear={() => {
          setSelected(new Set());
          anchorRef.current = null;
        }}
      />

      <BulkPublishDialog
        ids={selectedIds}
        reviewCount={
          products.filter((product) => selected.has(product.id) && product.needsReview).length
        }
        open={publishOpen}
        onOpenChange={setPublishOpen}
        onPublished={(outcome) => {
          reportOutcome(outcome, 'gepubliceerd');
          setSelected(new Set());
          void load();
        }}
      />

      <ProductReviewersDialog
        open={reviewersOpen}
        products={products.filter((product) => selected.has(product.id))}
        onOpenChange={setReviewersOpen}
        onSaved={() => {
          toast.success('Reviewers opgeslagen');
          void load();
        }}
        onError={(message, error) => toast.error(message, { error })}
        onManageGroups={() => {
          setReviewersOpen(false);
          setGroupsOpen(true);
        }}
      />

      <ReviewerGroupsDialog open={groupsOpen} onOpenChange={setGroupsOpen} onChanged={load} />

      <RatesDialog open={ratesOpen} onOpenChange={setRatesOpen} onChanged={load} />

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nieuw product</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="new-name">Naam</Label>
            <Input
              id="new-name"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && handleCreate()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleCreate}>Aanmaken</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProductManagementPage;
